import { AccountController } from '@app/api/controller/models/account-controller.model';
import { ActivityItem } from '@app/api/activity-report/models/activity-report.models';
import { Constants } from '@app/shared/models/icentral-constants.model';
import { ToroEnums } from '@app/common/enumerations/toro.enums';
import { TranslateService } from '@ngx-translate/core';

import ActivityCategory = ToroEnums.ActivityCategory;
import ActivityStatus = ToroEnums.ActivityStatus;
import ActivitySubCategory = ToroEnums.ActivitySubCategory;

// How an ActivityItem payload turns into the text and icon a row shows. Shared by the Activity Report row and the
// header Activity panel (AB#20613) so the two can never label the same activity differently, which is how the old
// panel drifted onto a six-value type list.

export function resolveActivityStatus(activity: ActivityItem): ActivityStatus {
    const status = activity?.status;
    return typeof status === 'number' && Number.isFinite(status) ? status as ActivityStatus : ActivityStatus.Unknown;
}

export function getActivityStatusModifier(status: ActivityStatus): string {
    return Constants.ACTIVITY_STATUS_MODIFIER[status] ?? 'information';
}

export function getActivityStatusIcon(status: ActivityStatus): string {
    return Constants.ACTIVITY_STATUS_ICON[status] ?? 'activity-information';
}

/** Filter key of the activity's status, as used by the report's Status filter. Empty when the status is missing. */
export function getActivityStatusFilterKey(activity: ActivityItem): string {
    const payload = activity as any;
    const rawStatus = payload?.status ?? payload?.Status;
    if (rawStatus == null) { return ''; }

    const status = Number(rawStatus);
    return Number.isFinite(status) ? Constants.ACTIVITY_STATUS_FILTER_KEY_MAP[status as ActivityStatus] || '' : '';
}

/** "MM/DD/YY · hh:mm AM" in local time, e.g. "10/02/26 · 07:55 AM". */
export function formatActivityTimestamp(timestamp: string): string {
    if (!timestamp) { return ''; }

    const date = new Date(timestamp);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, '0');

    return `${month}/${day}/${year} · ${formattedHours}:${minutes} ${ampm}`;
}

/** "Category · Subcategory", falling back to whichever exists, then to the payload title. */
export function getActivityTitle(activity: ActivityItem, ts: TranslateService): string {
    const categoryLabel = getCategoryLabel(activity, ts);
    const subCategoryLabel = getSubCategoryLabel(activity, ts);

    // An alarm reads by what it is, "Power On · Controller Alarms", rather than "Alarm · Controller Alarms" (AB#21734).
    // The type is already in the stored details; a Clear All, which names no alarm, keeps the category.
    const isAlarm = getEnumValue<ActivityCategory>(ActivityCategory, activity.category) === ActivityCategory.Alarm;
    const alertType = isAlarm ? activity.description?.alertType?.trim() : '';
    if (alertType && subCategoryLabel) {
        return `${alertType} · ${subCategoryLabel}`;
    }

    if (categoryLabel && subCategoryLabel) {
        return `${categoryLabel} · ${subCategoryLabel}`;
    }

    return subCategoryLabel || categoryLabel || activity.title || '';
}

/** True when any activity names a controller id but not its name or type - the report payload often does. */
export function hasMissingControllerDetails(activities: ActivityItem[]): boolean {
    if (!activities || activities.length === 0) {
        return false;
    }

    return activities.some((activity: ActivityItem) => {
        const controller = activity?.controller;
        if (!controller?.id) {
            return false;
        }

        const hasName = !!controller.name;
        const hasType = controller.type != null;
        return !hasName || !hasType;
    });
}

/** Fills each activity's missing controller name and type from the account's controllers, in place. */
export function mergeControllerDetails(activities: ActivityItem[], accountControllers: AccountController[]): void {
    if (!activities || activities.length === 0 || !accountControllers || accountControllers.length === 0) {
        return;
    }

    const accountControllerById = new Map<string, AccountController>(
        accountControllers
            .filter((controller: AccountController) => !!controller?.id)
            .map((controller: AccountController) => [controller.id, controller])
    );

    for (const activity of activities) {
        const activityController = activity?.controller;
        if (!activityController?.id) {
            continue;
        }

        const accountController = accountControllerById.get(activityController.id);
        if (!accountController) {
            continue;
        }

        if (!activityController.name) {
            activityController.name = accountController.name || '';
        }

        if (activityController.type == null && accountController.controllerTypeId != null) {
            activityController.type = accountController.controllerTypeId;
        }
    }
}

function getCategoryLabel(activity: ActivityItem, ts: TranslateService): string {
    const category = getEnumValue<ActivityCategory>(ActivityCategory, activity.category);
    if (category == null || category === ActivityCategory.Unknown) { return ''; }

    const key = Constants.ACTIVITY_CATEGORY_LABEL[category];
    return key ? ts.instant(key) : '';
}

function getSubCategoryLabel(activity: ActivityItem, ts: TranslateService): string {
    const subCategory = getEnumValue<ActivitySubCategory>(ActivitySubCategory, activity.subCategory);
    if (subCategory == null || subCategory === ActivitySubCategory.Unknown) { return ''; }

    const key = Constants.ACTIVITY_SUB_CATEGORY_LABEL[subCategory];
    return key ? ts.instant(key) : '';
}

function getEnumValue<TEnum extends number>(enumType: any, candidate: any): TEnum | null {
    if (candidate == null) {
        return null;
    }

    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate as TEnum;
    }

    if (typeof candidate === 'string') {
        const asNumber = Number(candidate);
        if (!Number.isNaN(asNumber)) {
            return asNumber as TEnum;
        }

        const enumValue = enumType[candidate];
        if (typeof enumValue === 'number') {
            return enumValue as TEnum;
        }
    }

    return null;
}
